import { useState, useEffect, useCallback, useImperativeHandle, useRef, ForwardedRef } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'

export interface CarouselHandle {
    goToSlide: (index: number) => void
    next: () => void
    prev: () => void
    getCurrentSlide: () => number
    stopAutoplay: () => void
    resumeAutoplay: () => void
    isAutoPlaying: boolean
}

interface UseCarouselProps {
    onSlideChange?: (index: number) => void
    initialSlide?: number
}

export const useCarousel = (
    emblaApi: EmblaCarouselType | undefined,
    ref?: ForwardedRef<CarouselHandle>,
    { onSlideChange, initialSlide = 0 }: UseCarouselProps = {},
) => {
    const [currentSlide, setCurrentSlide] = useState(initialSlide)
    const [isPlaying, setIsPlaying] = useState(false)

    // Store onSlideChange in a ref to avoid triggering re-renders
    const onSlideChangeRef = useRef(onSlideChange)
    useEffect(() => {
        onSlideChangeRef.current = onSlideChange
    }, [onSlideChange])

    // Memoized functions to avoid unnecessary re-renders
    const goToSlide = useCallback(
        (index: number) => {
            if (emblaApi) emblaApi.scrollTo(index)
        },
        [emblaApi],
    )

    const next = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext()
    }, [emblaApi])

    const prev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev()
    }, [emblaApi])

    const stopAutoplay = useCallback(() => {
        if (emblaApi) {
            const autoplay = emblaApi.plugins()?.autoplay as any
            if (autoplay) autoplay.stop()
            setIsPlaying(false)
        }
    }, [emblaApi])

    const resumeAutoplay = useCallback(() => {
        if (emblaApi) {
            const autoplay = emblaApi.plugins()?.autoplay as any
            if (autoplay) autoplay.play()
            setIsPlaying(true)
        }
    }, [emblaApi])

    // Subscribe to embla select event
    useEffect(() => {
        if (!emblaApi) return

        const onSelect = () => {
            const newSlide = emblaApi.selectedScrollSnap()
            setCurrentSlide(newSlide)
            if (onSlideChangeRef.current) {
                onSlideChangeRef.current(newSlide)
            }
        }

        emblaApi.on('select', onSelect)
        onSelect() // Call once on mount

        return () => {
            emblaApi.off('select', onSelect)
        }
    }, [emblaApi])

    useImperativeHandle(
        ref,
        () => ({
            goToSlide,
            next,
            prev,
            getCurrentSlide: () => currentSlide,
            stopAutoplay,
            resumeAutoplay,
            isAutoPlaying: isPlaying,
        }),
        [goToSlide, next, prev, currentSlide, stopAutoplay, resumeAutoplay, isPlaying],
    )

    return {
        currentSlide,
        goToSlide,
        next,
        prev,
        stopAutoplay,
        resumeAutoplay,
        isAutoPlaying: isPlaying,
    }
}
